import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { msgErro } from "@/lib/erros";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import logoUrl from "/milkymoo.webp?url";
import { useMarca } from "@/hooks/useMarca";
import { logAuthCliente } from "@/lib/auth-log";
import { usaLoginGoogle } from "@/lib/auth-modo";

import { obterClienteDados } from "@/lib/obter-dados";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Entrar — Milky Moo Palmares" },
      { name: "description", content: "Acesse o controle de estoque da sua sorveteria." },
      { property: "og:title", content: "Entrar — Milky Moo Palmares" },
      { property: "og:description", content: "Acesse o controle de estoque da sua sorveteria." },
    ],
  }),
  component: AuthPage,
});

async function getClienteDados() {
  return (await obterClienteDados()) as any;
}

async function bloquearSeSuspenso() {
  logAuthCliente("Verificação de suspensão iniciada", {
    origem: "public.users",
  });
  const cliente = await getClienteDados();
  const { data } = await cliente.auth.getUser();
  if (!data.user) {
    logAuthCliente("Verificação de suspensão sem usuário autenticado");
    return false;
  }
  const nivel = Number(data.user.user_metadata?.["nivel"] ?? 1);
  logAuthCliente("Nível lido após o login", {
    usuarioId: data.user.id,
    email: data.user.email,
    nivelMetadados: data.user.user_metadata?.["nivel"] ?? null,
    nivelEfetivo: nivel ?? null,
  });
  if (Number(nivel ?? 9) === 5) {
    await cliente.auth.signOut({ scope: "local" });
    toast.error("Seu acesso está suspenso. Fale com um administrador.");
    return true;
  }
  return false;
}

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [senhaLogin, setSenhaLogin] = useState("");
  const [senhaCriar, setSenhaCriar] = useState("");
  const [capsLock, setCapsLock] = useState(false);
  const [capsHintVisible, setCapsHintVisible] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    // Global listener: update capsLock state whenever any key is pressed anywhere
    // in the page. This helps detect CapsLock if the user already toggled it
    // before focusing the password field but pressed another key on the page.
    function onKey(e: KeyboardEvent) {
      try {
        setCapsLock(Boolean(e.getModifierState && e.getModifierState("CapsLock")));
      } catch {
        // ignore
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);
  const [nome, setNome] = useState("");
  const [loading, setLoading] = useState(false);
  const { nomeApp, permitirCadastro } = useMarca();

  useEffect(() => {
    (async () => {
      const cliente = await getClienteDados();
      const { data } = await cliente.auth.getSession();
      if (!data.session) return;
      if (await bloquearSeSuspenso()) return;
      navigate({ to: "/inicio" });
    })();
  }, [navigate]);

  // Clear any browser autofill value placed into the password input that
  // doesn't update React state. Some browsers fill the input visually but
  // don't trigger React change events; clearing the DOM value prevents an
  // already-filled password from appearing in the login form.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const el = document.getElementById("senha") as HTMLInputElement | null;
        if (el) el.value = "";
      } catch {
        // ignore
      }
    }, 50);
    return () => clearTimeout(t);
  }, []);

  async function entrar(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setFormError(null);
    logAuthCliente("Formulário de login enviado", {
      email: email.trim(),
      origem: "public.users",
      senhaInformada: senhaLogin.length > 0,
      tamanhoSenha: senhaLogin.length,
    });
    const cliente = await getClienteDados();
    const { error } = await cliente.auth.signInWithPassword({ email, password: senhaLogin });
    setLoading(false);
    if (error) {
      logAuthCliente("Login retornou erro", { mensagem: error.message, status: error.status });
      const capsHint = capsLock ? " Verifique se o CapsLock está ligado." : "";
      setFormError(msgErro(error) + capsHint);
      return;
    }
    logAuthCliente("Login retornou sucesso; iniciando validação de nível");
    if (await bloquearSeSuspenso()) return;
    navigate({ to: "/inicio" });
  }

  async function entrarGoogle() {
    // Redirect to server start endpoint to initiate OAuth flow
    setFormError(null);
    try {
      window.location.href = "/auth/google/start";
    } catch (e) {
      setFormError("Falha ao iniciar login via Google.");
    }
  }

  async function cadastrar(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const cliente = await getClienteDados();
    const { error } = await cliente.auth.signUp({
      email,
      password: senhaCriar,
      options: { data: { nome } },
    });
    setLoading(false);
    if (error) {
      toast.error(msgErro(error));
      return;
    }
    toast.success("Conta criada! Você já pode entrar.");
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-[10px] py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2 text-primary">
          <img src={logoUrl} alt={nomeApp} className="h-32 w-32 object-contain" />
          <span className="font-display text-[1.6875rem] leading-tight font-semibold">
            {nomeApp}
          </span>
        </div>

        <div className="card-soft p-6">
          <Tabs defaultValue="entrar">
            <TabsList className="w-full">
              <TabsTrigger value="entrar" className="flex-1">
                Entrar
              </TabsTrigger>
              {permitirCadastro && (
                <TabsTrigger value="criar" className="flex-1">
                  Criar conta
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="entrar">
              <form onSubmit={entrar} className="space-y-4 pt-4" autoComplete="off">
                {/* Dummy inputs to discourage browser autofill managers from
                    filling the visible password field. These are off-screen
                    but present in the DOM so password managers prefer them. */}
                <div
                  style={{
                    position: "absolute",
                    left: -9999,
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                  }}
                  aria-hidden
                >
                  <input type="text" name="fake-username" autoComplete="username" tabIndex={-1} />
                  <input
                    type="password"
                    name="fake-password"
                    autoComplete="current-password"
                    tabIndex={-1}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    required
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="senha">Senha</Label>
                  <Input
                    id="senha"
                    type="password"
                    name="senhaLogin"
                    autoComplete="new-password"
                    value={senhaLogin}
                    required
                    onChange={(e) => {
                      setSenhaLogin(e.target.value);
                      if (formError) setFormError(null);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      try {
                        if ((e as any).getModifierState && (e as any).getModifierState("CapsLock"))
                          setCapsLock(true);
                        else setCapsLock(false);
                      } catch {
                        setCapsLock(false);
                      }
                    }}
                    onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      try {
                        if ((e as any).getModifierState && (e as any).getModifierState("CapsLock"))
                          setCapsLock(true);
                        else setCapsLock(false);
                      } catch {
                        setCapsLock(false);
                      }
                    }}
                    onFocus={() => {
                      // If we don't yet know caps state, show a short hint asking
                      // user to press a key to check (some browsers won't expose
                      // the state without a key event).
                      if (!capsLock) setCapsHintVisible(true);
                    }}
                  />
                  {capsLock && (
                    <div className="text-sm text-yellow-700">
                      CapsLock está ligado — verifique maiúsculas.
                    </div>
                  )}
                  {!capsLock && capsHintVisible && (
                    <div className="text-sm text-muted-foreground">
                      Se a senha não entrar, pressione qualquer tecla para detectar CapsLock.
                    </div>
                  )}
                </div>
                {formError && <div className="text-sm text-red-700">{formError}</div>}
                <Button type="submit" className="w-full" disabled={loading}>
                  Entrar
                </Button>
                {usaLoginGoogle() && (
                  <>
                    {/* Separador isolando a área de e-mail/senha da área de login social. */}
                    <div className="my-4 flex items-center gap-3">
                      <Separator className="flex-1" />
                      <span className="text-xs uppercase text-muted-foreground">ou</span>
                      <Separator className="flex-1" />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={entrarGoogle}
                      className="w-full"
                      disabled={loading}
                    >
                      Continuar com Google
                    </Button>
                  </>
                )}
              </form>
            </TabsContent>

            {permitirCadastro && (
              <TabsContent value="criar">
                <form onSubmit={cadastrar} className="space-y-4 pt-4" autoComplete="off">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome</Label>
                    <Input
                      id="nome"
                      value={nome}
                      required
                      onChange={(e) => setNome(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email2">E-mail</Label>
                    <Input
                      id="email2"
                      type="email"
                      name="email2"
                      autoComplete="email"
                      value={email}
                      required
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="senha2">Senha</Label>
                    <Input
                      id="senha2"
                      type="password"
                      name="senhaCriar"
                      autoComplete="new-password"
                      value={senhaCriar}
                      required
                      minLength={6}
                      onChange={(e) => setSenhaCriar(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    Criar conta
                  </Button>
                </form>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </main>
  );
}
